import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { buildYouTubeEmbedUrl, getYouTubeVideoId } from '../services/assetsService';

const richContentSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    'iframe',
    'video',
    'source',
  ],
  attributes: {
    ...(defaultSchema.attributes || {}),
    a: [
      ...(defaultSchema.attributes?.a || []),
      'target',
      'rel',
    ],
    iframe: [
      'allow',
      'allowfullscreen',
      'allowFullScreen',
      'height',
      'loading',
      'referrerpolicy',
      'referrerPolicy',
      'src',
      'title',
      'width',
    ],
    img: [
      ...(defaultSchema.attributes?.img || []),
      'data-align',
      'data-width',
      'height',
      'loading',
      'title',
      'width',
    ],
    video: [
      'autoplay',
      'controls',
      'height',
      'loop',
      'muted',
      'playsinline',
      'playsInline',
      'poster',
      'preload',
      'src',
      'width',
    ],
    source: [
      'src',
      'type',
    ],
  },
  protocols: {
    ...(defaultSchema.protocols || {}),
    src: ['http', 'https'],
  },
};

const normaliseUrl = (value = '') => {
  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) return '';
  if (/^https?:\/\//i.test(trimmedValue)) return trimmedValue;
  return `https://${trimmedValue}`;
};

const isSafeHttpUrl = (value = '') => {
  try {
    const url = new URL(normaliseUrl(value));
    return ['http:', 'https:'].includes(url.protocol);
  } catch (error) {
    return false;
  }
};

const externalLinkProps = {
  rel: 'noreferrer',
  target: '_blank',
};

const imageTitleOptions = (title = '') => Object.fromEntries(
  String(title || '')
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [key, ...valueParts] = part.split('=');
      return [key.trim().toLowerCase(), valueParts.join('=').trim()];
    })
    .filter(([key, value]) => key && value)
);

const normaliseImageAlign = (value = '') => (
  ['left', 'center', 'right'].includes(String(value).toLowerCase())
    ? String(value).toLowerCase()
    : ''
);

const normaliseImageWidth = (value = '') => {
  const width = String(value || '').trim();
  if (!width) return '';
  if (/^\d{1,4}px$/.test(width)) return width;
  if (/^\d{1,3}%$/.test(width)) return width;
  return '';
};

const YouTubeEmbed = ({ title = 'YouTube video', url }) => {
  const videoId = getYouTubeVideoId(url);
  const embedUrl = buildYouTubeEmbedUrl(videoId);
  if (!embedUrl) return null;

  return (
    <span className="rich-content-embed rich-content-youtube-embed">
      <iframe
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        src={embedUrl}
        title={title}
      />
    </span>
  );
};

const richContentComponents = {
  a: ({ href = '', children, node, ...props }) => {
    const youtubeVideoId = getYouTubeVideoId(href);

    if (youtubeVideoId) {
      const linkText = React.Children.toArray(children)
        .map((child) => (typeof child === 'string' ? child : ''))
        .join('')
        .trim();

      return (
        <YouTubeEmbed
          title={linkText && linkText !== href ? linkText : 'YouTube video'}
          url={href}
        />
      );
    }

    return (
      <a href={href} {...externalLinkProps} {...props}>
        {children}
      </a>
    );
  },
  iframe: ({ src = '', title = 'Embedded content', node, ...props }) => {
    if (!isSafeHttpUrl(src)) return null;

    return (
      <span className="rich-content-embed rich-content-web-embed">
        <iframe
          {...props}
          allow={props.allow || 'fullscreen'}
          allowFullScreen={Boolean(props.allowFullScreen || props.allowfullscreen)}
          loading={props.loading || 'lazy'}
          referrerPolicy={props.referrerPolicy || props.referrerpolicy || 'no-referrer-when-downgrade'}
          src={normaliseUrl(src)}
          title={title}
        />
      </span>
    );
  },
  img: ({ alt = '', node, title = '', ...props }) => {
    const titleOptions = imageTitleOptions(title);
    const align = normaliseImageAlign(props['data-align'] || titleOptions.align);
    const width = normaliseImageWidth(props['data-width'] || props.width || titleOptions.width);
    const imageProps = { ...props };
    delete imageProps['data-align'];
    delete imageProps['data-width'];

    return (
      <span
        className={`rich-content-image ${align ? `align-${align}` : ''}`.trim()}
        style={width ? { width, maxWidth: '100%' } : undefined}
      >
        <img
          alt={alt}
          loading="lazy"
          {...imageProps}
        />
      </span>
    );
  },
  table: (props) => {
    const tableProps = { ...props };
    delete tableProps.node;

    return (
      <div className="asset-markdown-table-scroll">
        <table {...tableProps} />
      </div>
    );
  },
  video: ({ children, node, ...props }) => (
    <video controls playsInline preload="metadata" {...props}>
      {children}
    </video>
  ),
};

function RichContent({ articleRef, children, className = '' }) {
  return (
    <article className={`asset-markdown-article rich-content ${className}`.trim()} ref={articleRef}>
      <ReactMarkdown
        components={richContentComponents}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, richContentSchema]]}
        remarkPlugins={[remarkGfm]}
      >
        {children || ''}
      </ReactMarkdown>
    </article>
  );
}

export default RichContent;
